import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';
import type { ColorVariant } from '@/theme/tokens';

export type StatusIndicatorProps = HTMLAttributes<HTMLSpanElement> & {
  variant?: ColorVariant;
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  pulse?: boolean;
};

const dotColorClasses: Record<ColorVariant, string> = {
  primary: 'bg-primary',
  teal: 'bg-teal',
  neutral: 'bg-muted-foreground',
  muted: 'bg-muted-foreground',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  info: 'bg-info',
};

const dotSizeClasses = {
  sm: 'size-1.5',
  md: 'size-2',
  lg: 'size-2.5',
};

function StatusIndicator({
  variant = 'neutral',
  children,
  className,
  size = 'md',
  pulse = false,
  ...rest
}: StatusIndicatorProps) {
  return (
    <span
      data-slot="status-indicator"
      className={cn('inline-flex min-w-0 items-center gap-2', className)}
      {...rest}
    >
      <span
        className={cn(
          'shrink-0 rounded-full',
          dotSizeClasses[size],
          dotColorClasses[variant],
          pulse && 'animate-pulse'
        )}
      />
      <span className="truncate">{children}</span>
    </span>
  );
}

export { StatusIndicator };
export default StatusIndicator;
